/**
 * Event landing-page template — 02-speaker ("Bauhaus Summit").
 *
 * Distinct CraftJS home-page document for the `speaker` theme. Cloned from the
 * proven `webinar` template (same node structure, factories and contracts).
 * Authored from the design spec in
 *   TEMPLATES - LANDINPAGES/02-speaker/sections/*.html (CRAFT_PROPS_HINT blocks).
 *
 * Visual style — "bauhaus-summit": bold Bauhaus primary colours
 * (red/blue/yellow accents) on a warm-cream/white background with black ink,
 * a geometric Futura-like sans, and hard rectangular edges (rounded-none).
 *
 * Convention (mirrors temp.ts/webinar):
 *  - Section-level marketing copy (headlines, paragraphs, button labels) goes
 *    through td() keys (`spk*` prefix) → localized.
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also
 *    goes through td() keys — the *_DATA arrays are functions of td.
 *  - Global colors/fonts come from the speakerPreset (applied via the theme);
 *    only sections with their own surface (Hero/Testimonials over dark) set
 *    explicit per-node colors here.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Speaker-specific asset namespace (seeded under <CDN>/speaker/…)
const W = `${A}/shared/templates/02-speaker`;

// ── Bauhaus-summit palette ────────────────────────────────────────────
const RED = "#cc1f1f"; // Bauhaus red — primary accent (darkened from #ef4444 for WCAG: cream-on-red 5.35:1, eyebrow-on-cream 5.35:1)
const BLUE = "#2563eb"; // Bauhaus blue — secondary accent
const YELLOW = "#fbbf24"; // Bauhaus yellow — tertiary accent
const INK = "#1a1a1a"; // near-black headings / borders
const MUTE = "#3f3f46"; // muted body
const CREAM = "#fffbeb"; // warm surface (page background)
const DARK = "#1a1a1a"; // dark section surface
const ON_DARK = "#e7e5e4"; // body text on dark
const ON_DARK_ACCENT = YELLOW; // eyebrow on dark
// Futura-like geometric sans for headings; Helvetica-grotesk body.
const SANS = "Futura, 'Century Gothic', 'Helvetica Neue', Arial, sans-serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Inline child data (localized via td, matches 02-speaker copy) ─────

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>12</b>", desc: td("spkNum1Desc") },
  { num: "<b>8</b>", desc: td("spkNum2Desc") },
  { num: "<b>10h</b>", desc: td("spkNum3Desc") },
  { num: "<b>320</b>", desc: td("spkNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { time: `<b>${td("spkAgendaRow1Time")}</b>`, title: td("spkAgendaRow1Title"), speaker: td("spkAgendaRow1Who"), desc: td("spkAgendaRow1Desc") },
  { time: `<b>${td("spkAgendaRow2Time")}</b>`, title: td("spkAgendaRow2Title"), speaker: td("spkAgendaRow2Who"), desc: td("spkAgendaRow2Desc") },
  { time: `<b>${td("spkAgendaRow3Time")}</b>`, title: td("spkAgendaRow3Title"), speaker: td("spkAgendaRow3Who"), desc: td("spkAgendaRow3Desc") },
  { time: `<b>${td("spkAgendaRow4Time")}</b>`, title: td("spkAgendaRow4Title"), speaker: td("spkAgendaRow4Who"), desc: td("spkAgendaRow4Desc") },
  { time: `<b>${td("spkAgendaRow5Time")}</b>`, title: td("spkAgendaRow5Title"), speaker: td("spkAgendaRow5Who"), desc: td("spkAgendaRow5Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: "Prof. Mira Halász", role: td("spkSpeaker1Role"), img: 0 },
  { name: "Jonas Brandt", role: td("spkSpeaker2Role"), img: 1 },
  { name: "Sami El-Tayeb", role: td("spkSpeaker3Role"), img: 2 },
  { name: "Dr. Anna Reichmann", role: td("spkSpeaker4Role"), img: 3 },
  { name: "Marc Lefèvre", role: td("spkSpeaker5Role"), img: 4 },
  { name: "Yara Costa", role: td("spkSpeaker6Role"), img: 5 },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("spkTesti1Quote"), name: "Lena Marquardt", role: td("spkTesti1Role"), img: 0 },
  { quote: td("spkTesti2Quote"), name: "Tobias Reichert", role: td("spkTesti2Role"), img: 1 },
  { quote: td("spkTesti3Quote"), name: "Sophia Mendes", role: td("spkTesti3Role"), img: 2 },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("spkBlog1Topic")}</b>`, content: td("spkBlog1Content") },
  { topic: `<b>${td("spkBlog2Topic")}</b>`, content: td("spkBlog2Content") },
  { topic: `<b>${td("spkBlog3Topic")}</b>`, content: td("spkBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("spkFaq1Question")}</b>`, a: td("spkFaq1Answer") },
  { q: `<b>${td("spkFaq2Question")}</b>`, a: td("spkFaq2Answer") },
  { q: `<b>${td("spkFaq3Question")}</b>`, a: td("spkFaq3Answer") },
  { q: `<b>${td("spkFaq4Question")}</b>`, a: td("spkFaq4Answer") },
  { q: `<b>${td("spkFaq5Question")}</b>`, a: td("spkFaq5Answer") },
  { q: `<b>${td("spkFaq6Question")}</b>`, a: td("spkFaq6Answer") },
];

// ── Speaker-specific child factories ──────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[d.img % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[d.img % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/hero-zusage.jpg`, `${W}/hero-dokumente.jpg`,
  `${W}/hero-treatment.jpg`, `${W}/hero-save-the-date.jpg`, `${W}/hero-absage.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const speaker = (td: TranslationFn): string => {
  const B = "spk_body";

  // Top-level section ids (body order)
  const HERO = "spk_hero";
  const NUMBERS = "spk_numbers";
  const AGENDA = "spk_agenda";
  const SPEAKER = "spk_speaker";
  const TEXT = "spk_text";
  const IMAGE = "spk_image";
  const COUNTDOWN = "spk_countdown";
  const GALLERY = "spk_gallery";
  const VIDEO = "spk_video";
  const TESTI = "spk_testimonials";
  const BLOG = "spk_blog";
  const FAQ = "spk_faq";
  const MAP = "spk_map";
  const DI = "spk_datainput";
  const CONTACT = "spk_contact";

  return enc({
    ...scaffold(
      { ab: "spk_ab", abs: "spk_abs", body: B, ft: "spk_ft", fts: "spk_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-none" },
    ),

    // ── Hero (image right, dark ink on warm cream) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        // imageLayout:"right" → image on the right, text left (desktop, ≥wb-md).
        // inversed:true → on mobile the column stacks text-first, image below.
        sectionHeight: 90, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("spkHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: RED,
        isVisibleHeader: true, textHeader: td("spkHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("spkHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("spkHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("spkHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: CREAM,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "spk_hero_eb", header: "spk_hero_hd", text: "spk_hero_tx", buttonOne: "spk_hero_b1", buttonTwo: "spk_hero_b2" },
    },
    spk_hero_eb: tc(HERO, { isVisible: true, text: td("spkHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: RED, header: true }),
    spk_hero_hd: tc(HERO, { isVisible: true, text: td("spkHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    spk_hero_tx: tc(HERO, { isVisible: true, text: td("spkHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    spk_hero_b1: btn(HERO, { type: "secondary", text: td("spkHeroSecondaryButton"), displayName: "buttonOne" }),
    spk_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("spkHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["spk_num1", "spk_num2", "spk_num3", "spk_num4"],
      linkedNodes: { headline: "spk_num_eb", header: "spk_num_hd", text: "spk_num_tx" },
    },
    spk_num_eb: tc(NUMBERS, { isVisible: false, text: td("spkNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_num_hd: tc(NUMBERS, { isVisible: true, text: td("spkNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_num_tx: tc(NUMBERS, { isVisible: true, text: td("spkNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_num1: numbersChild(NUMBERS, 0, td),
    spk_num2: numbersChild(NUMBERS, 1, td),
    spk_num3: numbersChild(NUMBERS, 2, td),
    spk_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["spk_ag1", "spk_ag2", "spk_ag3", "spk_ag4", "spk_ag5"],
      linkedNodes: { headline: "spk_ag_eb", header: "spk_ag_hd", text: "spk_ag_tx" },
    },
    spk_ag_eb: tc(AGENDA, { isVisible: false, text: td("spkAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_ag_hd: tc(AGENDA, { isVisible: true, text: td("spkAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_ag_tx: tc(AGENDA, { isVisible: true, text: td("spkAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_ag1: agendaChild(AGENDA, 0, td),
    spk_ag2: agendaChild(AGENDA, 1, td),
    spk_ag3: agendaChild(AGENDA, 2, td),
    spk_ag4: agendaChild(AGENDA, 3, td),
    spk_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (image library hidden by default) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["spk_sp1", "spk_sp2", "spk_sp3", "spk_sp4", "spk_sp5", "spk_sp6"],
      linkedNodes: { headline: "spk_sp_eb", header: "spk_sp_hd", text: "spk_sp_tx" },
    },
    spk_sp_eb: tc(SPEAKER, { isVisible: false, text: td("spkSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_sp_hd: tc(SPEAKER, { isVisible: true, text: td("spkSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_sp_tx: tc(SPEAKER, { isVisible: true, text: td("spkSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_sp1: speakerChild(SPEAKER, 0, td),
    spk_sp2: speakerChild(SPEAKER, 1, td),
    spk_sp3: speakerChild(SPEAKER, 2, td),
    spk_sp4: speakerChild(SPEAKER, 3, td),
    spk_sp5: speakerChild(SPEAKER, 4, td),
    spk_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (the manifesto) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("spkTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("spkTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "spk_tx_eb", header: "spk_tx_hd", text: "spk_tx_tx", buttonOne: "spk_tx_b1", buttonTwo: "spk_tx_b2" },
    },
    spk_tx_eb: tc(TEXT, { isVisible: false, text: td("spkTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_tx_hd: tc(TEXT, { isVisible: true, text: td("spkTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    spk_tx_tx: tc(TEXT, { isVisible: true, text: td("spkTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_tx_b1: btn(TEXT, { type: "secondary", text: td("spkTextSecondaryButton"), displayName: "buttonOne" }),
    spk_tx_b2: btn(TEXT, { type: "primary", text: td("spkTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (audio/tech detail, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("spkImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("spkImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("spkImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("spkImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-none", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/hero-treatment.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "spk_im_eb", header: "spk_im_hd", text: "spk_im_tx", button: "spk_im_btn", image: "spk_im_img" },
    },
    spk_im_eb: tc(IMAGE, { isVisible: false, text: td("spkImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    spk_im_hd: tc(IMAGE, { isVisible: true, text: td("spkImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    spk_im_tx: tc(IMAGE, { isVisible: true, text: td("spkImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    spk_im_btn: btn(IMAGE, { text: td("spkImageButton"), displayName: "button" }),
    spk_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/hero-treatment.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown (ticket window) ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("spkCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("spkCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("spkCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("spkCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SANS, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: "#52525b", countdownBorderColor: INK,
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "spk_cd_eb", header: "spk_cd_hd", text: "spk_cd_tx", button: "spk_cd_btn" },
    },
    spk_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("spkCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: RED, header: true }),
    spk_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("spkCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    spk_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("spkCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    spk_cd_btn: btn(COUNTDOWN, { text: td("spkCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (backstage) — hidden by default (no photo library) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("spkGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("spkGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("spkGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["spk_g1", "spk_g2", "spk_g3", "spk_g4", "spk_g5", "spk_g6"],
      linkedNodes: { headline: "spk_g_eb", header: "spk_g_hd", text: "spk_g_tx", secondary: "spk_g_b2", primary: "spk_g_b1" },
    },
    spk_g_eb: tc(GALLERY, { isVisible: false, text: td("spkGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_g_hd: tc(GALLERY, { isVisible: true, text: td("spkGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_g_tx: tc(GALLERY, { isVisible: false, text: td("spkGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_g_b2: btn(GALLERY, { type: "secondary", text: td("spkGallerySecondaryButton"), displayName: "ButtonChild" }),
    spk_g_b1: btn(GALLERY, { text: td("spkGalleryPrimaryButton"), displayName: "ButtonChild" }),
    spk_g1: galleryImage(GALLERY, 0),
    spk_g2: galleryImage(GALLERY, 1),
    spk_g3: galleryImage(GALLERY, 2),
    spk_g4: galleryImage(GALLERY, 3),
    spk_g5: galleryImage(GALLERY, 4),
    spk_g6: galleryImage(GALLERY, 5),

    // ── Video (manifesto recap) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("spkVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("spkVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "spk_vd_eb", header: "spk_vd_hd", text: "spk_vd_tx", button: "spk_vd_btn" },
    },
    spk_vd_eb: tc(VIDEO, { isVisible: false, text: td("spkVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_vd_hd: tc(VIDEO, { isVisible: true, text: td("spkVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_vd_tx: tc(VIDEO, { isVisible: true, text: td("spkVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_vd_btn: btn(VIDEO, { text: td("spkVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("spkTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("spkTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["spk_ts1", "spk_ts2", "spk_ts3"],
      linkedNodes: { headline: "spk_ts_eb", header: "spk_ts_hd", text: "spk_ts_tx" },
    },
    spk_ts_eb: tc(TESTI, { isVisible: true, text: td("spkTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    spk_ts_hd: tc(TESTI, { isVisible: true, text: td("spkTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    spk_ts_tx: tc(TESTI, { isVisible: false, text: td("spkTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    spk_ts1: testimonialChild(TESTI, 0, td),
    spk_ts2: testimonialChild(TESTI, 1, td),
    spk_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (pre-reading) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["spk_bl1", "spk_bl2", "spk_bl3"],
      linkedNodes: { headline: "spk_bl_eb", header: "spk_bl_hd", text: "spk_bl_tx" },
    },
    spk_bl_eb: tc(BLOG, { isVisible: false, text: td("spkBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_bl_hd: tc(BLOG, { isVisible: true, text: td("spkBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_bl_tx: tc(BLOG, { isVisible: true, text: td("spkBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_bl1: blogChild(BLOG, 0, td),
    spk_bl2: blogChild(BLOG, 1, td),
    spk_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("spkFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["spk_fq1", "spk_fq2", "spk_fq3", "spk_fq4", "spk_fq5", "spk_fq6"],
      linkedNodes: { headline: "spk_fq_eb", header: "spk_fq_hd", text: "spk_fq_tx" },
    },
    spk_fq_eb: tc(FAQ, { isVisible: false, text: td("spkFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    spk_fq_hd: tc(FAQ, { isVisible: true, text: td("spkFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    spk_fq_tx: tc(FAQ, { isVisible: true, text: td("spkFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    spk_fq1: faqChild(FAQ, 0, td),
    spk_fq2: faqChild(FAQ, 1, td),
    spk_fq3: faqChild(FAQ, 2, td),
    spk_fq4: faqChild(FAQ, 3, td),
    spk_fq5: faqChild(FAQ, 4, td),
    spk_fq6: faqChild(FAQ, 5, td),

    // ── Map (Bauhaus-Saal, Dessau) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("spkMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("spkMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("spkMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("spkMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-none",
        mapZoom: 14, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("spkMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "spk_mp_eb", header: "spk_mp_hd", text: "spk_mp_tx", button: "spk_mp_btn" },
    },
    spk_mp_eb: tc(MAP, { isVisible: false, text: td("spkMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    spk_mp_hd: tc(MAP, { isVisible: true, text: td("spkMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    spk_mp_tx: tc(MAP, { isVisible: true, text: td("spkMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    spk_mp_btn: btn(MAP, { text: td("spkMapButton"), displayName: "button" }),

    // ── DataInput (multi-step speaker profile) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("spkDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("spkDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("spkDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("spkStepBasis"), td("spkStepProfile"), td("spkStepSession"), td("spkStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: RED,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "spk_di_hl", header: "spk_di_hd", text: "spk_di_tx",
        fieldsGroup0: "spk_di_f0", fieldsGroup1: "spk_di_f1", fieldsGroup2: "spk_di_f2",
        fieldsGroup3: "spk_di_f3", fieldsGroup4: "spk_di_f4",
        companionsGroup: "spk_di_comp", overviewGroup: "spk_di_ov", checkboxes: "spk_di_cb",
        button: "spk_di_btn", backButton: "spk_di_back", nextButton: "spk_di_next",
      },
    },
    spk_di_hl: tc(DI, { isVisible: true, text: td("spkDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    spk_di_hd: tc(DI, { isVisible: true, text: td("spkDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    spk_di_tx: tc(DI, { isVisible: true, text: td("spkDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    spk_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["spk_di_acceptance", "spk_di_first", "spk_di_last", "spk_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    spk_di_acceptance: acceptanceFi("spk_di_f0", td("acceptanceState")),
    spk_di_first: fi("spk_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    spk_di_last: fi("spk_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    spk_di_email: fi("spk_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    spk_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["spk_di_studio", "spk_di_role"], linkedNodes: {},
    },
    spk_di_studio: fi("spk_di_f1", { label: td("spkFieldStudio"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "studio", changeable: true }),
    spk_di_role: fi("spk_di_f1", { label: td("spkFieldRole"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "role", changeable: true }),
    spk_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["spk_di_portfolio"], linkedNodes: {},
    },
    spk_di_portfolio: fi("spk_di_f2", { label: td("spkFieldPortfolio"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "portfolio", gridArea: "col-span-12", changeable: true }),
    spk_di_f3: emptyFieldsGroup(DI, 3),
    spk_di_f4: emptyFieldsGroup(DI, 4),
    spk_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["spk_di_comp_first"], linkedNodes: {},
    },
    spk_di_comp_first: fi("spk_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    spk_di_ov_t: tc("spk_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    spk_di_ov_x: tc("spk_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    spk_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["spk_di_ov_t", "spk_di_ov_x"], linkedNodes: {},
    },
    spk_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    spk_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("spkDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    spk_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    spk_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("spkContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("spkContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("spkContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "instagram", url: "https://instagram.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("spkContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "spk_ct_eb", header: "spk_ct_hd", text: "spk_ct_tx",
        firstName: "spk_ct_first", lastName: "spk_ct_last", eMail: "spk_ct_email",
        phoneNumber: "spk_ct_phone", Message: "spk_ct_msg", button: "spk_ct_btn",
      },
    },
    spk_ct_eb: tc(CONTACT, { isVisible: true, text: td("spkContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: RED, header: true }),
    spk_ct_hd: tc(CONTACT, { isVisible: true, text: td("spkContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    spk_ct_tx: tc(CONTACT, { isVisible: true, text: td("spkContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    spk_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    spk_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    spk_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    spk_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    spk_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    spk_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("spkContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Warm-cream bar, black top rule, logo + nav + "Ticket sichern" CTA. */
export const speakerAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: CREAM,
      border: true, borderColor: INK, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("spkLinkProgram"), url: "#spk_agenda", linkType: "internal" },
        { label: td("spkLinkSpeakers"), url: "#spk_speaker", linkType: "internal" },
        { label: td("spkLinkManifest"), url: "#spk_text", linkType: "internal" },
        { label: td("spkLinkContact"), url: "#spk_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("spkHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: RED, logoutButtonColor: CREAM, logoutButtonBorderColor: INK,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark footer, red top rule, four link groups + social + copyright. No logo. */
export const speakerFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      // border-4 + borderSides:["top"] → getBorderClasses emits "border-t-4"
      // (passing "border-t-4" as the width would mis-emit "border-t-t-4").
      border: true, borderColor: RED, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      // No footer logo (per design) — omit "logo" from sectionOrder + hide it.
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("spkFooterProgram"), links: [
          { label: td("spkLinkSpeakers"), url: "#spk_speaker", linkType: "internal" },
          { label: td("spkLinkAgenda"), url: "#spk_agenda", linkType: "internal" },
          { label: td("spkLinkManifest"), url: "#spk_text", linkType: "internal" },
          { label: td("spkLinkReplay"), url: "/login", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in a
        // new tab). URLs are placeholders the organiser edits per event.
        { title: td("spkFooterEvent"), links: [
          { label: td("spkLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Bauhaus+Dessau", linkType: "external" },
          { label: td("spkLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Bauhaus+Summit+2026", linkType: "external" },
          { label: td("spkLinkTickets"), url: "/register", linkType: "internal" },
        ] },
        { title: td("spkFooterSupport"), links: [
          { label: td("spkLinkContact"), url: "#spk_contact", linkType: "internal" },
          { label: td("spkLinkFaq"), url: "#spk_faq", linkType: "internal" },
          { label: td("spkLinkPress"), url: "#spk_contact", linkType: "internal" },
        ] },
        { title: td("spkFooterLegal"), links: [
          { label: td("spkLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("spkLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("spkLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#a1a1aa", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
        { altText: "youtube", platform: "youtube", url: "https://youtube.com" },
      ],
      socialColor: "#a1a1aa", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#71717a", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B3) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero stays a light cream sheet (image right, node props); the css
// layers the bold-community poster treatment on top: a stark ink grid
// ornament in the top-right corner, an editorial red rule that draws itself
// under the headline block, uppercase letterspaced kickers, and the
// self-hosted Jost faces registered AS "Futura" so the preset's geometric
// stack actually resolves ('Helvetica Neue' body is a system face — no
// webfont needed, the stack falls back fine).
const CUSTOM_CSS = `/* theme:speaker */
${fontFaces([
  { family: "Futura", file: "jost-regular", weightRange: "400" },
  { family: "Futura", file: "jost-bold", weightRange: "700" },
  { family: "Futura", file: "jost-black", weightRange: "900" },
])}

/* Bauhaus hero — clip + isolate the grid/rule ornament layers */
#spk_hero {
${heroShell}
}

/* Stark construction grid in the top-right corner (ink hairlines on cream) */
#spk_hero::before {
${decorLayer}
  top: 0;
  right: 0;
  width: 280px;
  height: 280px;
  background-image:
    repeating-linear-gradient(0deg, rgba(26, 26, 26, 0.12) 0, rgba(26, 26, 26, 0.12) 1px, transparent 1px, transparent 40px),
    repeating-linear-gradient(90deg, rgba(26, 26, 26, 0.12) 0, rgba(26, 26, 26, 0.12) 1px, transparent 1px, transparent 40px);
  -webkit-mask-image: linear-gradient(225deg, #000 30%, transparent 82%);
  mask-image: linear-gradient(225deg, #000 30%, transparent 82%);
}

/* Editorial red rule beneath the headline block (Bauhaus bar, not hairline) */
#spk_hero::after {
${decorLayer}
  left: 6%;
  bottom: 12%;
  width: 140px;
  height: 4px;
  background: ${RED};
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#spk_hero .text-xs, #spk_numbers .text-xs, #spk_agenda .text-xs, #spk_speaker .text-xs,
#spk_text .text-xs, #spk_image .text-xs, #spk_countdown .text-xs, #spk_gallery .text-xs,
#spk_video .text-xs, #spk_testimonials .text-xs, #spk_blog .text-xs, #spk_faq .text-xs,
#spk_map .text-xs, #spk_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("spk", 18)}
  @keyframes spkRuleGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }
  @keyframes spkGridIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Poster entrance: the sheet settles, the red bar draws itself, the grid
     construction lines fade in behind it */
  #spk_hero { animation: spkFadeUp 0.7s ${EASE} both; }
  #spk_hero::after {
    transform-origin: left center;
    animation: spkRuleGrow 1.1s ${EASE} 0.4s both;
  }
  #spk_hero::before { animation: spkGridIn 1.4s ${EASE} 0.6s both; }

${revealBlock("spk", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────
// "bauhaus-summit": Bauhaus red primary, blue secondary, warm-cream surface,
// geometric Futura-like sans, hard rectangular edges (rounded-none).
export const speakerPreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: RED, secondary: BLUE, background: CREAM },
  fonts: { header: SANS, body: "'Helvetica Neue', Arial, sans-serif" },
  rounding: "rounded-none",
  recommendedSize: "large",
  bodyTheme: { fontColor: MUTE, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: SANS, fontSize: "44px", fontColor: INK },
    h2: { fontFamily: SANS, fontSize: "26px", fontColor: INK },
  },
  buttonsTheme: {
    primary: {
      bgColor: RED, bgHoverColor: INK, borderColor: INK, borderHoverColor: INK,
      fontColor: CREAM, fontHoverColor: YELLOW, fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-none", hoverBorderRadius: "rounded-none",
    },
    // Ghost secondary per P9 (solid primary stays the single loud CTA):
    // transparent on cream, ink border/text; hover fills the Bauhaus yellow.
    secondary: {
      bgColor: "transparent", bgHoverColor: YELLOW, borderColor: INK, borderHoverColor: INK,
      fontColor: INK, fontHoverColor: INK, fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-none", hoverBorderRadius: "rounded-none",
    },
  },
  hyperlinkTheme: { fontColor: BLUE, fontHoverColor: RED, underline: false },
  formInputTheme: {
    labelFontColor: INK, inputFontColor: INK, backgroundColor: "#FFFFFF",
    border: true, borderColor: INK, borderRadius: "rounded-none", borderStyle: "border-solid",
    borderWidth: "border", borderSides: [], fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontSize: "text-sm", errorColor: RED,
  },
  customCss: CUSTOM_CSS,
};
